import {announce} from '@github-ui/aria-live'
import {handleRepositoryDataError} from '@github-ui/data-router-error-helpers'
import {applyIssueViewerFieldValue} from '@github-ui/issue-metadata/issue-field-value-optimistic'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {
  type IssueViewerPayload,
  issueViewerPrimaryQueryOptions,
  type IssueViewerQueryIdentifier,
  persistIssueViewerPrimary,
} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {keepPreviousData, useQuery} from '@github-ui/react-query'
import {applyIssueSummaryDelta} from '@github-ui/sub-issues/optimistic-overlay'
import {AlertIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useSidePanelScrollReset} from '../hooks/use-side-panel-scroll-reset'
import type {IssueReference} from '../types/issue'
import {applyPendingSuggestionOperation} from '../utils/suggestion-ref'
import {DataRouterIssueViewer} from './DataRouterIssueViewer'
import {IssueViewerLoading} from './IssueViewerLoading'
import {OptimisticUpdateRetry} from './OptimisticUpdateRetry'
import type {OptionConfig} from './OptionConfig'
import {useTimelinePrefill} from './timeline/timeline-prefill'

const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']
const ISSUE_LOAD_ERROR_MESSAGE = "This issue couldn't be loaded here."

export type TanStackIssueViewerProps = {
  optionConfig: OptionConfig
  /** `?timeline_page=N`, for the crawler's isolated historical window. */
  timelinePage?: number
  /** Artifact number selected by an issue artifact route. */
  linkedArtifactNumber?: number
} & IssueReference

type ViewerPresentation = {
  reference: IssueReference
  queryIdentifier: IssueViewerQueryIdentifier
  queryKey: readonly unknown[]
  optionConfig: OptionConfig
  payloadUpdatedAt: number
  timelinePage?: number
  linkedArtifactNumber?: number
}

export function getIssueViewerQueryError(query: {
  data: unknown
  error: Error | null
  isError: boolean
  isPlaceholderData?: boolean
}) {
  return query.isError && (!query.data || query.isPlaceholderData)
    ? (query.error ?? new Error('Issue viewer query failed'))
    : undefined
}

export function TanStackIssueViewer(props: TanStackIssueViewerProps) {
  const {linkedArtifactNumber, number, optionConfig, timelinePage} = props
  const reference = useMemo<IssueReference>(
    () =>
      props.isAccountIssue
        ? {
            isAccountIssue: true,
            number,
            permalink: props.permalink,
            ...(props.owner ? {owner: props.owner} : {}),
            ...(props.repo ? {repo: props.repo} : {}),
          }
        : {owner: props.owner, repo: props.repo, number},
    [number, props.isAccountIssue, props.owner, props.permalink, props.repo],
  )
  const queryIdentifier = useMemo(() => {
    if (reference.isAccountIssue) return {permalink: reference.permalink}
    return {owner: reference.owner, repo: reference.repo, number}
  }, [number, reference])
  const queryOptions = useMemo(() => issueViewerPrimaryQueryOptions(queryIdentifier), [queryIdentifier])
  const query = useQuery({
    ...queryOptions,
    staleTime: optionConfig.insideSidePanel ? 0 : queryOptions.staleTime,
    placeholderData: optionConfig.insideSidePanel ? keepPreviousData : undefined,
  })
  const {refetch} = query
  const retryQuery = useCallback(() => {
    void refetch()
  }, [refetch])
  const requestedPresentation = useMemo<ViewerPresentation>(
    () => ({
      reference,
      queryIdentifier,
      queryKey: queryOptions.queryKey,
      optionConfig,
      payloadUpdatedAt: query.dataUpdatedAt,
      timelinePage,
      linkedArtifactNumber,
    }),
    [
      linkedArtifactNumber,
      optionConfig,
      query.dataUpdatedAt,
      queryIdentifier,
      queryOptions.queryKey,
      reference,
      timelinePage,
    ],
  )
  const [retainedPresentation, setRetainedPresentation] = useState<ViewerPresentation>()

  if (query.data !== undefined && !query.isPlaceholderData && retainedPresentation !== requestedPresentation) {
    setRetainedPresentation(requestedPresentation)
  }

  const queryError = getIssueViewerQueryError(query)
  if (queryError && retainedPresentation !== undefined) {
    setRetainedPresentation(undefined)
  }
  const displayedPayload = query.data as IssueViewerPayload | undefined
  const displayedIssue = displayedPayload?.data?.repository?.issue
  const retainedReference = retainedPresentation?.reference
  const displayedOptionConfig =
    query.data !== undefined && !query.isPlaceholderData
      ? requestedPresentation.optionConfig
      : retainedPresentation?.optionConfig
  useSidePanelScrollReset(
    Boolean(displayedOptionConfig?.insideSidePanel && (displayedIssue || retainedReference)),
    displayedIssue?.repository.owner.login ?? retainedReference?.owner ?? '',
    displayedIssue?.repository.name ?? retainedReference?.repo ?? retainedReference?.permalink ?? '',
    displayedIssue?.number ?? retainedReference?.number ?? 0,
  )
  const isOptimisticRetryActive =
    optionConfig.subIssueFromOptimisticUpdate &&
    query.errorUpdatedAt > 0 &&
    (queryError !== undefined || query.isPending || query.isPlaceholderData)
  if (isOptimisticRetryActive) {
    return (
      <OptimisticUpdateRetry key={issueReferenceHref(reference)} retryQuery={retryQuery} optionConfig={optionConfig} />
    )
  }
  if (queryError) {
    if (optionConfig.insideSidePanel) {
      return <EmbeddedIssueViewerError error={queryError} reference={reference} />
    }
    throw queryError
  }
  if (query.isPending) return <IssueViewerLoading optionConfig={optionConfig} />

  const presentation = query.isPlaceholderData ? retainedPresentation : requestedPresentation
  const payload = query.data as IssueViewerPayload | undefined
  if (!payload || !presentation) {
    if (query.isPlaceholderData) return <IssueViewerLoading optionConfig={optionConfig} />
    throw new Error('Issue viewer query returned no payload')
  }

  const viewer = <TanStackIssueViewerResult payload={payload} presentation={presentation} retryQuery={retryQuery} />

  return presentation.optionConfig.insideSidePanel ? (
    <ErrorBoundary
      key={issueReferenceHref(presentation.reference)}
      boundaryName="TanStackIssueViewer"
      fallback={<IssueViewerLoadError reference={presentation.reference} />}
    >
      {viewer}
    </ErrorBoundary>
  ) : (
    viewer
  )
}

function TanStackIssueViewerResult({
  payload,
  presentation,
  retryQuery,
}: {
  payload: IssueViewerPayload
  presentation: ViewerPresentation
  retryQuery: () => void
}) {
  const {optionConfig, queryIdentifier, queryKey} = presentation
  const presentedQueryOptions = useMemo(() => issueViewerPrimaryQueryOptions(queryIdentifier), [queryIdentifier])
  const presentedQuery = useQuery({...presentedQueryOptions, enabled: false})
  const currentPayload = (presentedQuery.data as IssueViewerPayload | undefined) ?? payload
  const currentPayloadUpdatedAt = presentedQuery.dataUpdatedAt || presentation.payloadUpdatedAt
  const persistPrimaryAfterSettlement = useCallback(
    ({queryClient}: {queryClient: Parameters<typeof persistIssueViewerPrimary>[1]}) =>
      persistIssueViewerPrimary(queryIdentifier, queryClient),
    [queryIdentifier],
  )
  const mainQueryTargets = useMemo(
    () => [{queryKey, onDocumentSettled: persistPrimaryAfterSettlement}],
    [persistPrimaryAfterSettlement, queryKey],
  )
  const issueId = currentPayload?.data?.repository?.issue?.id
  const projectItemsConnections = useMemo(
    () => (issueId ? [{ownerId: issueId, connectionPath: PROJECT_ITEMS_CONNECTION_PATH}] : []),
    [issueId],
  )
  const renderedPayload = useOptimisticData(currentPayload, {
    connections: projectItemsConnections,
    targets: mainQueryTargets,
    applyEntityFieldItem: applyIssueViewerFieldValue,
    applyEntityFieldValuesDelta: applyIssueSummaryDelta,
    updateEntityFieldItems: applyPendingSuggestionOperation,
  })

  const repository = renderedPayload?.data?.repository
  const issue = repository?.issue
  const timelineFrontIssue = useMemo(
    () => (issue?.timelineItems ? {id: issue.id, timelineItems: issue.timelineItems} : undefined),
    [issue],
  )
  const timelineBackIssue = useMemo(
    () => (issue?.backTimelineItems ? {id: issue.id, timelineItems: issue.backTimelineItems} : undefined),
    [issue],
  )
  const timelineCoordinates = useMemo(
    () =>
      issue
        ? {
            owner: issue.repository.owner.login,
            repo: issue.repository.name,
            number: issue.number,
          }
        : undefined,
    [issue],
  )
  const initialBackAnchor = useTimelinePrefill({
    coordinates: timelineCoordinates,
    page: presentation.timelinePage,
    payloadPage: currentPayload.timelinePage,
    issue: timelineFrontIssue,
    backIssue: timelineBackIssue,
    payloadUpdatedAt: currentPayloadUpdatedAt,
    feed: currentPayload.feed,
  })

  if (optionConfig.subIssueFromOptimisticUpdate && !renderedPayload?.data?.repository?.issue) {
    return <OptimisticUpdateRetry retryQuery={retryQuery} optionConfig={optionConfig} />
  }

  const data = handleRepositoryDataError(renderedPayload, {
    shouldReloadOnError: !optionConfig.insideSidePanel,
  })
  if (!data) return null

  const resolvedRepository = data?.repository
  const resolvedIssue = resolvedRepository?.issue
  if (!resolvedIssue) {
    const reference = presentation.reference.isAccountIssue
      ? presentation.reference.permalink
      : `${presentation.reference.owner}/${presentation.reference.repo}#${presentation.reference.number}`
    throw new Error(`Issue missing from repository data for ${reference}`)
  }

  return (
    <DataRouterIssueViewer
      key={resolvedIssue.id}
      issue={resolvedIssue}
      feed={currentPayload.feed}
      initialBackAnchor={initialBackAnchor}
      viewer={data?.safeViewer ?? null}
      isRepoOwnerEnterpriseManaged={resolvedRepository?.isOwnerEnterpriseManaged}
      userNameDisplayConfiguration={resolvedRepository.owner.userNameDisplayConfiguration}
      routeQueryKey={queryKey}
      timelinePage={presentation.timelinePage}
      linkedArtifactNumber={presentation.linkedArtifactNumber}
      optionConfig={optionConfig}
    />
  )
}

function EmbeddedIssueViewerError({error, reference}: {error: Error; reference: IssueReference}) {
  return (
    <ErrorBoundary
      key={issueReferenceHref(reference)}
      boundaryName="TanStackIssueViewerQuery"
      fallback={<IssueViewerLoadError reference={reference} />}
    >
      <IssueViewerErrorThrower error={error} />
    </ErrorBoundary>
  )
}

function IssueViewerErrorThrower({error}: {error: Error}): never {
  throw error
}

export function IssueViewerLoadError({reference}: {reference: IssueReference}) {
  const href = issueReferenceHref(reference)
  const recoveryLinkRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    const recoveryLink = recoveryLinkRef.current
    if (!recoveryLink) return

    recoveryLink.focus()
    announce(ISSUE_LOAD_ERROR_MESSAGE, {element: recoveryLink})
  }, [])

  return (
    <div className="d-flex flex-column flex-items-start gap-2 p-4 f5 color-fg-muted">
      <span className="d-inline-flex flex-items-center gap-2">
        <AlertIcon aria-hidden="true" className="color-fg-attention" />
        {ISSUE_LOAD_ERROR_MESSAGE}
      </span>
      <Link ref={recoveryLinkRef} href={href}>
        Open issue #{reference.number}
      </Link>
    </div>
  )
}

function issueReferenceHref(reference: IssueReference): string {
  if (!reference.isAccountIssue) {
    return `/${reference.owner}/${reference.repo}/issues/${reference.number}`
  }

  try {
    const url = new URL(reference.permalink, 'https://github.com')
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '/'
  }
}
